import { motion, useInView } from "motion/react";
import { ReactNode, useRef } from "react";
import { useScrollBehavior } from "@/hooks/useScrollBehavior";

interface Props {
  children: ReactNode;
  className?: string;
}

/**
 * Wraps CTA content with idle-aware emphasis.
 * When user pauses scrolling near the CTA, a subtle warm glow 
 * intensifies around it — drawing gentle attention without aggression.
 */
const AdaptiveCta = ({ children, className = "" }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isIdle, scrollSpeed } = useScrollBehavior();
  const isInView = useInView(ref, {
    margin: "-20% 0px -20% 0px",
    amount: 0.3,
  });

  // Only activate when CTA is in view AND user has paused or is scrolling slowly
  const isAttentive = isInView && (isIdle || scrollSpeed === "slow");

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Idle-responsive ambient glow behind CTA — larger, warmer */}
      <motion.div
        className="absolute -inset-x-12 -inset-y-6 pointer-events-none z-0"
        initial={false}
        animate={{
          opacity: isAttentive ? 1 : 0,
          scale: isAttentive ? 1 : 0.92,
        }}
        transition={{
          duration: isAttentive ? 2.5 : 1.8,
          ease: [0.22, 0.68, 0.35, 1],
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_50%,hsl(32_35%_52%_/_0.05)_0%,transparent_70%)] blur-[3px]" />
      </motion.div>
      <div className="relative z-[1]">{children}</div>
    </div>
  );
};

export default AdaptiveCta;
